import React, { useState } from "react";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

import { Tooltip } from "@/components/common/Tooltip";
import { TextInput } from "components/common/TextInput";
import { SelectCheckbox } from "components/common/SelectCheckbox";
import { SslModePicker } from "components/main/utils/settings/SslModePicker";
import type { SslMode } from "components/canvas/leftSide/import/Postgres/postgresApi";

// Mirrors `MAX_DISPLAY_NAME_LEN` in saved_connection_store.
export const MAX_NAME_LEN = 64;

/** Shared form shape for the Postgres-protocol editors (Postgres + Supabase).
 *  `clear_ca_cert` is edit-only and stays false on create. */
export interface PgFormState {
  display_name: string;
  host: string;
  port: number;
  database: string;
  username: string;
  password: string;
  ssl_mode: SslMode;
  ca_cert_pem: string;
  clear_ca_cert: boolean;
}

/** A custom CA cert is only consulted in VerifyFull mode; Encrypt skips
 *  verification entirely. Drop a pasted cert otherwise (ignored server-side). */
export const caCertForSubmit = (form: PgFormState): string | undefined =>
  form.ssl_mode === "VerifyFull" && form.ca_cert_pem.trim().length > 0
    ? form.ca_cert_pem
    : undefined;

export const Field: React.FC<{
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  type?: "text" | "password" | "number";
  disabled?: boolean;
  /** Tooltip text shown via an info icon beside the label. */
  info?: string;
  /** Render the info icon in the warning color (used for high-risk gotchas). */
  infoWarning?: boolean;
}> = ({
  label,
  value,
  onChange,
  placeholder,
  type = "text",
  disabled,
  info,
  infoWarning,
}) => (
  <label className="flex flex-col gap-1 min-w-0">
    <span className="flex items-center gap-1 text-fg-muted-medium">
      {label}
      {info && (
        <Tooltip title={info}>
          <InfoOutlinedIcon
            sx={{ fontSize: 14 }}
            className={`cursor-help ${infoWarning ? "text-warning" : "text-fg-muted-medium"}`}
          />
        </Tooltip>
      )}
    </span>
    <TextInput
      type={type}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      autoComplete="off"
      disabled={disabled}
    />
  </label>
);

/** Host / Port+Database / Username / Password block shared by both editors.
 *  The Name field stays in the parent (placement differs per editor). */
export const PgCredentialFields: React.FC<{
  form: PgFormState;
  update: (patch: Partial<PgFormState>) => void;
  /** Logical-identity fields are immutable post-create (edit mode). */
  disabled?: boolean;
  hostPlaceholder?: string;
  hostInfo?: string;
  portInfo?: string;
  usernameInfo?: string;
  usernameInfoWarning?: boolean;
  passwordLabel: string;
}> = ({
  form,
  update,
  disabled,
  hostPlaceholder,
  hostInfo,
  portInfo,
  usernameInfo,
  usernameInfoWarning,
  passwordLabel,
}) => (
  <>
    <Field
      label="Host"
      value={form.host}
      onChange={(v) => update({ host: v })}
      placeholder={hostPlaceholder}
      info={hostInfo}
      disabled={disabled}
    />
    <div className="grid grid-cols-[80px_1fr] gap-2">
      <Field
        label="Port"
        value={form.port === 0 ? "" : String(form.port)}
        onChange={(v) => {
          if (v === "") return update({ port: 0 });
          if (!/^\d+$/.test(v)) return;
          update({ port: Number(v) });
        }}
        info={portInfo}
        disabled={disabled}
      />
      <Field
        label="Database"
        value={form.database}
        onChange={(v) => update({ database: v })}
        disabled={disabled}
      />
    </div>
    <Field
      label="Username"
      value={form.username}
      onChange={(v) => update({ username: v })}
      info={usernameInfo}
      infoWarning={usernameInfoWarning}
      disabled={disabled}
    />
    <Field
      label={passwordLabel}
      type="password"
      value={form.password}
      onChange={(v) => update({ password: v })}
    />
  </>
);

/** SSL mode picker plus the VerifyFull-only Advanced custom-CA disclosure.
 *  `showClearCaToggle` adds the edit-only "Clear stored CA certificate"
 *  affordance (and its replace hint) when the existing connection has a
 *  stored cert. */
export const SslSection: React.FC<{
  form: PgFormState;
  update: (patch: Partial<PgFormState>) => void;
  showClearCaToggle?: boolean;
  /** Helper line under the CA textarea. Defaults to the generic
   *  private/self-signed-CA guidance; Supabase passes its own. */
  caHelpText?: React.ReactNode;
}> = ({
  form,
  update,
  showClearCaToggle = false,
  caHelpText = "Optional. Use if your server has a private or self-signed CA the system trust store doesn't recognise.",
}) => {
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const verifies = form.ssl_mode === "VerifyFull";

  return (
    <>
      <div className="mt-1">
        <SslModePicker
          value={form.ssl_mode}
          onChange={(ssl_mode) => update({ ssl_mode })}
        />
      </div>
      {verifies && (
        <div className="flex flex-col gap-1 mt-1">
          <button
            type="button"
            className="inline-flex items-center gap-1 text-fg-muted-semibold hover:text-fg w-fit cursor-pointer"
            onClick={() => setAdvancedOpen((v) => !v)}
          >
            {advancedOpen ? (
              <ExpandMoreIcon sx={{ fontSize: 14 }} />
            ) : (
              <ChevronRightIcon sx={{ fontSize: 14 }} />
            )}
            Advanced
          </button>
          {advancedOpen && (
            <div className="flex flex-col gap-2 min-w-0">
              {showClearCaToggle && (
                <div className="flex items-center gap-2">
                  <SelectCheckbox
                    selected={form.clear_ca_cert}
                    onToggle={(e) => {
                      e.stopPropagation();
                      const next = !form.clear_ca_cert;
                      update({
                        clear_ca_cert: next,
                        ca_cert_pem: next ? "" : form.ca_cert_pem,
                      });
                    }}
                  />
                  <span className="text-fg-muted-medium">
                    Clear stored CA certificate
                  </span>
                </div>
              )}
              <label className="flex flex-col gap-1 min-w-0">
                <span className="text-fg-muted-medium">
                  Custom CA certificate (PEM)
                  {showClearCaToggle && (
                    <span className="text-fg-muted-regular-small ml-2">
                      — paste a new one to replace, or leave blank to keep
                    </span>
                  )}
                </span>
                <TextInput
                  multiline
                  minRows={4}
                  value={form.ca_cert_pem}
                  onChange={(v) =>
                    update({
                      ca_cert_pem: v,
                      clear_ca_cert: v.trim() ? false : form.clear_ca_cert,
                    })
                  }
                  placeholder={"-----BEGIN CERTIFICATE-----\n..."}
                />
                <span className="text-fg-muted-regular-small">
                  {caHelpText}
                </span>
              </label>
            </div>
          )}
        </div>
      )}
    </>
  );
};
